import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Stack,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
} from "@mui/icons-material";
import api from "../../conf/api";
import AudioCard from "./components/AudioCard";
import UploadDialog from "./components/UploadDialog";

function AudioManagement() {
  const [audioList, setAudioList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [audioToDelete, setAudioToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [audioToEdit, setAudioToEdit] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const fetchAudioList = useCallback(async (reset = false) => {
    if (loading) return;

    setLoading(true);
    try {
      const response = await api.get("/videos/audio/", {
        params: {
          page: reset ? 1 : page + 1,
          search: searchQuery,
          page_size: 12,
        },
      });

      if (response.data && response.data.results) {
        if (reset) {
          setAudioList(response.data.results);
          setPage(1);
        } else {
          setAudioList(prev => [...prev, ...response.data.results]);
          setPage(prev => prev + 1);
        }

        setHasMore(!!response.data.next);
      } else {
        // Handle non-paginated response
        setAudioList(Array.isArray(response.data) ? response.data : []);
        setHasMore(false);
      }
    } catch (error) {
      console.error("Failed to fetch audio files:", error);
      showSnackbar("Failed to fetch audio files", "error");
      setAudioList([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, page, loading]);

  useEffect(() => {
    fetchAudioList(true);
  }, [searchQuery]);

  const handleFilesSelected = (files) => {
    setUploadingFiles(files);
    files.forEach(uploadFile);
    // Keep dialog open to show upload progress
    // setUploadDialogOpen(false);
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    // Truncate title to 200 characters to match backend model
    const title = file.name.replace(/\.[^/.]+$/, "").substring(0, 200);
    formData.append("title", title);

    try {
      setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));

      const response = await api.post("/videos/audio/upload/", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 5 * 60 * 1000,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(prev => ({ ...prev, [file.name]: progress }));
        },
      });

      // Clean up upload tracking
      setUploadProgress(prev => {
        const updated = { ...prev };
        delete updated[file.name];
        return updated;
      });

      setUploadingFiles(prev => {
        const remainingFiles = prev.filter(f => f.name !== file.name);

        // Close dialog when all uploads are complete
        if (remainingFiles.length === 0) {
          setUploadDialogOpen(false);
        }

        return remainingFiles;
      });

      // Refresh the audio list
      fetchAudioList(true);
      showSnackbar(`${file.name} uploaded successfully!`, "success");

    } catch (error) {
      console.error(`Failed to upload ${file.name}:`, error);

      // Clean up upload tracking
      setUploadProgress(prev => {
        const updated = { ...prev };
        delete updated[file.name];
        return updated;
      });

      setUploadingFiles(prev => {
        const remainingFiles = prev.filter(f => f.name !== file.name);

        // Close dialog when all uploads are complete (even if some failed)
        if (remainingFiles.length === 0) {
          setUploadDialogOpen(false);
        }

        return remainingFiles;
      });

      showSnackbar(`Failed to upload ${file.name}`, "error");
    }
  };

  const handleDelete = async () => {
    if (!audioToDelete || isDeleting) return;

    console.log("Attempting to delete audio:", audioToDelete.id);
    setIsDeleting(true);

    try {
      const response = await api.delete(`/videos/audio/${audioToDelete.id}/`);
      console.log("Delete response:", response);

      setAudioList(prev => prev.filter(audio => audio.id !== audioToDelete.id));
      showSnackbar("Audio deleted successfully", "success");
    } catch (error) {
      console.error("Failed to delete audio:", error);
      console.error("Error response:", error.response);

      let errorMessage = "Failed to delete audio";
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status) {
        errorMessage = `Delete failed (${error.response.status}): ${error.response.statusText}`;
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setAudioToDelete(null);
    }
  };

  const handleEdit = (audio) => {
    setAudioToEdit(audio);
    setEditTitle(audio.title);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!audioToEdit || !editTitle.trim()) return;

    try {
      const response = await api.patch(`/videos/audio/${audioToEdit.id}/`, {
        title: editTitle.trim()
      });

      // Update the audio list with the new title
      setAudioList(prev =>
        prev.map(audio =>
          audio.id === audioToEdit.id
            ? { ...audio, title: editTitle.trim() }
            : audio
        )
      );

      showSnackbar("Audio title updated successfully", "success");
    } catch (error) {
      console.error("Failed to update audio title:", error);
      showSnackbar("Failed to update audio title", "error");
    } finally {
      setEditDialogOpen(false);
      setAudioToEdit(null);
      setEditTitle("");
    }
  };

  const handleDownload = (audio) => {
    if (audio.file) {
      const link = document.createElement('a');
      link.href = audio.file;
      link.download = audio.title || 'audio_file';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredAudio = audioList.filter(audio =>
    audio.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (audio.username && audio.username.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ mb: 1, fontWeight: 600 }}>
          🎵 Audio Management
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Manage your audio files with style and ease
        </Typography>
      </Box>

      {/* Search and Actions */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search audio files or users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                startIcon={<CloudUploadIcon />}
                onClick={() => setUploadDialogOpen(true)}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
                }}
              >
                Upload Audio
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading State */}
      {loading && audioList.length === 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Empty State */}
      {!loading && audioList.length === 0 && (
        <Paper sx={{ p: 8, textAlign: "center", borderRadius: 2 }}>
          <CloudUploadIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
            No Audio Files Found
          </Typography>
          <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
            {searchQuery
              ? `No audio files match your search for "${searchQuery}"`
              : "Upload your first audio file to get started"
            }
          </Typography>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
            sx={{ borderRadius: 2 }}
          >
            Upload Audio
          </Button>
        </Paper>
      )}

      {/* Audio Grid */}
      {filteredAudio.length > 0 && (
        <Grid container spacing={3}>
          {filteredAudio.map((audio) => (
            <Grid item xs={6} md={4} key={audio.id}>
              <AudioCard
                audio={audio}
                onDelete={(audio) => {
                  setAudioToDelete(audio);
                  setDeleteDialogOpen(true);
                }}
                onEdit={handleEdit}
                onDownload={handleDownload}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Load More */}
      {hasMore && !loading && filteredAudio.length > 0 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Button
            variant="outlined"
            onClick={() => fetchAudioList(false)}
            sx={{ borderRadius: 2, px: 4 }}
          >
            Load More
          </Button>
        </Box>
      )}

      {/* Upload Dialog */}
      <UploadDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onFilesSelected={handleFilesSelected}
        uploadingFiles={uploadingFiles}
        uploadProgress={uploadProgress}
      />

      {/* Delete Confirmation */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Confirm Deletion
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{audioToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ borderRadius: 2 }}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            sx={{ borderRadius: 2 }}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 2 } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Audio Title
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Title"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            placeholder="Enter audio title"
            inputProps={{ maxLength: 200 }}
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
            {editTitle.length}/200 characters
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            sx={{ borderRadius: 2 }}
            disabled={!editTitle.trim() || editTitle.trim() === audioToEdit?.title}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{
          position: "fixed",
          bottom: 24,
          right: 24,
          background: "linear-gradient(45deg, #FF6B6B 30%, #FF8E53 90%)",
        }}
        onClick={() => setUploadDialogOpen(true)}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
}

export default AudioManagement;